import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { ApiError } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { Button, Field, Input } from '../components/ui.tsx';

/**
 * Split layout rather than a centered card: the left half states what the
 * server does, the right half is the form. No marketing, no illustration - an
 * operator reaching this screen wants to be past it.
 */
export function LoginPage() {
  const { user, signIn } = useSession();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      await signIn(email, password);
      navigate('/', { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Der Server ist gerade nicht erreichbar.',
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="grid min-h-[100dvh] bg-base lg:grid-cols-[1.1fr_1fr]">
      <section className="hidden flex-col justify-between border-r border-line bg-sunken p-12 lg:flex">
        <span className="flex items-center gap-2">
          <span aria-hidden className="size-2 rounded-full bg-accent" />
          <span className="text-[15px] font-semibold tracking-tight text-ink">MeshAlert</span>
        </span>

        <div className="max-w-[38ch]">
          <h1 className="text-4xl leading-[1.1] font-semibold tracking-tight text-ink">
            Alarme aus allen Quellen an einer Stelle.
          </h1>
          <p className="mt-4 text-[14px] leading-relaxed text-ink-2">
            Der Server nimmt Alarme per Webhook oder API entgegen, entscheidet nach Regeln, wer sie
            bekommt, und stellt sie an iPhone, Mac oder ein weiteres System zu.
          </p>
        </div>

        <dl className="grid grid-cols-3 gap-6 border-t border-line pt-6">
          <div>
            <dt className="text-[11px] text-ink-3">Eingang</dt>
            <dd className="mt-0.5 text-[13px] text-ink">Webhook und API</dd>
          </div>
          <div>
            <dt className="text-[11px] text-ink-3">Zustellung</dt>
            <dd className="mt-0.5 text-[13px] text-ink">Push und Webhook</dd>
          </div>
          <div>
            <dt className="text-[11px] text-ink-3">Nachweis</dt>
            <dd className="mt-0.5 text-[13px] text-ink">Pro Gerät belegt</dd>
          </div>
        </dl>
      </section>

      <section className="flex items-center justify-center px-6 py-16">
        <form onSubmit={onSubmit} className="w-full max-w-[22rem] space-y-5">
          <div className="lg:hidden">
            <span className="flex items-center gap-2">
              <span aria-hidden className="size-2 rounded-full bg-accent" />
              <span className="text-[15px] font-semibold tracking-tight text-ink">MeshAlert</span>
            </span>
          </div>

          <div>
            <h2 className="text-lg font-semibold tracking-tight text-ink">Anmelden</h2>
            <p className="mt-1 text-[13px] text-ink-2">Zugang bekommst du von einem Admin.</p>
          </div>

          <Field label="E-Mail">
            {(id) => (
              <Input
                id={id}
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@meshalert.de"
              />
            )}
          </Field>

          <Field label="Passwort">
            {(id) => (
              <Input
                id={id}
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}
          </Field>

          {error ? (
            <p
              role="alert"
              className="rounded-md border border-critical/40 bg-critical-soft px-3 py-2 text-[13px] text-critical"
            >
              {error}
            </p>
          ) : null}

          <Button type="submit" variant="primary" disabled={pending} className="w-full">
            {pending ? 'Wird geprüft' : 'Anmelden'}
          </Button>
        </form>
      </section>
    </div>
  );
}
